import CloseIcon from '@mui/icons-material/Close';
import { Avatar, AvatarProps, Button, DialogTitle, IconButton, Skeleton } from '@mui/material';
import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Fade from '@mui/material/Fade';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { ReactElement } from 'react';
import { makeStyles } from 'tss-react/mui';
import useTeceoStyles from '../../useTeceoStyles';
import { getColorByRole } from '../../utils/generics';
import { SelectModalTestIds } from '../../utils/testIds/SelectModals.testIds';

const useStyles = makeStyles()((theme) => ({
  radius: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
  },
  avatar: {
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),

    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(4),
      height: theme.spacing(4),
    },
  },
  avatarBorder: {
    border: '0.5px solid',
    borderColor: theme.palette.text.secondary,
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  iconButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.action.active,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  title: {
    textTransform: 'uppercase',
  },
}));

type TeamSelectModalWrapperProps = {
  children: ReactElement<any, any> | undefined;
  headerMessage: string;
  isLoading?: boolean;
  onClose: () => void;
  open: boolean;
  title: string;
};

type ListItemButtonProps = {
  handleClick: () => void;
  avatarContent: string | React.ReactNode;
  avatarAlt?: string;
  avatarSrc?: string;
  avatarProps?: AvatarProps;
  typographyProps?: TypographyProps;
  name: string;
  style?: React.CSSProperties | undefined;
  testId?: string;
};

export const ListItemButton = ({
  avatarAlt,
  avatarContent,
  avatarProps = {},
  avatarSrc,
  handleClick,
  name,
  style,
  typographyProps = {},
  testId,
}: ListItemButtonProps) => {
  const { classes, cx } = useStyles();

  return (
    <Grid size={12}>
      <Button onClick={handleClick} fullWidth test-id={testId}>
        <Grid
          wrap="nowrap"
          container
          size="grow"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <Grid>
            <Avatar
              src={avatarSrc}
              alt={avatarAlt}
              className={cx(classes.avatar, avatarSrc && classes.avatarBorder)}
              style={
                style || {
                  backgroundColor: avatarSrc
                    ? 'transparent'
                    : getColorByRole({ userRole: 'ADMIN_MARCA' }),
                }
              }
              {...avatarProps}
            >
              {avatarContent}
            </Avatar>
          </Grid>
          <Grid size="grow">
            <Typography align="left" {...typographyProps}>
              {name}
            </Typography>
          </Grid>
        </Grid>
      </Button>
    </Grid>
  );
};

const TeamSelectModalWrapper = ({
  children,
  open,
  headerMessage,
  onClose,
  isLoading = false,
  title,
}: TeamSelectModalWrapperProps) => {
  const { classes } = useStyles();
  const { classes: teceoClasses } = useTeceoStyles();

  return (
    <Dialog classes={{ paper: classes.radius }} open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>
        <IconButton
          size="small"
          onClick={onClose}
          className={classes.iconButton}
          test-id={SelectModalTestIds.CLOSE_BUTTON}
        >
          <CloseIcon color="action" />
        </IconButton>
        <Typography
          align="center"
          color="textPrimary"
          className={teceoClasses.uppercase}
          test-id={SelectModalTestIds.TITLE}
        >
          <b>{title}</b>
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid
          container
          spacing={3}
          sx={{
            justifyContent: 'center',
          }}
        >
          <Grid size={12}>
            <Typography test-id={SelectModalTestIds.HEADER_TEXT}>{headerMessage}</Typography>
          </Grid>
          {isLoading && (
            <Grid size={12}>
              <Grid
                container
                spacing={2}
                sx={{
                  justifyContent: 'center',
                }}
              >
                {[0, 1, 2, 3, 4, 5].map((item) => (
                  <Grid key={item} size={12}>
                    <Box
                      sx={{
                        paddingX: 1,
                      }}
                    >
                      <Grid
                        wrap="nowrap"
                        container
                        spacing={1}
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Grid>
                          <Skeleton variant="circular" width={32} height={32} />
                        </Grid>
                        <Grid size="grow">
                          <Skeleton className={classes.radius} variant="rectangular" height={40} />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
          {!isLoading && (
            <Grid size={12}>
              <Fade in>
                <div>{children}</div>
              </Fade>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default TeamSelectModalWrapper;
